import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SSE } from 'sse.js';

import { UploadProcessingEventType } from '../../../backend/src/storage/upload-processing-events';
import { UploadFailureReason, UploadProcessingStatus } from '../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

export interface UploadProgressError {
  id?: string;
  filename: string;
  rejectedReason: UploadFailureReason;
  parentFileId?: string;
  createdDate?: string;
}

export interface ExtractionProgress {
  filename: string;
  totalEntries: number;
  completedEntries: number;
  isComplete: boolean;
  completedAt?: number;
}

export interface UploadProgressState {
  status: 'idle' | UploadProcessingStatus;
  extractions: ExtractionProgress[];
  totalFiles: number;
  hashedFiles: number;
  duplicateCount: number;
  unpackingSkippedCount: number;
  errors: UploadProgressError[];
  downloadedFiles: number;
  totalDownloadFiles: number;
  preparingFetchedCount: number;
  preparingTotalCount: number;
}

const initialState: UploadProgressState = {
  status: 'idle',
  extractions: [],
  totalFiles: 0,
  hashedFiles: 0,
  duplicateCount: 0,
  unpackingSkippedCount: 0,
  errors: [],
  downloadedFiles: 0,
  totalDownloadFiles: 0,
  preparingFetchedCount: 0,
  preparingTotalCount: 0,
};

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 500;
const THROTTLE_INTERVAL_MS = 500;

/**
 * Reducer that folds a single SSE progress event into the current upload state.
 * Buffered events are batched and reduced together via `messages.reduce(reduceProgressEvent, current)`.
 */
function reduceProgressEvent(prev: UploadProgressState, data: any): UploadProgressState {
  switch (data.type as UploadProcessingEventType) {
    case UploadProcessingEventType.RelativityPreparingProgress:
      return {
        ...prev,
        preparingFetchedCount: data.fetchedCount ?? prev.preparingFetchedCount,
        preparingTotalCount: data.totalCount ?? prev.preparingTotalCount,
      };

    case UploadProcessingEventType.RelativityFileDownloaded:
      return {
        ...prev,
        downloadedFiles: data.completedCount ?? prev.downloadedFiles,
        totalDownloadFiles: data.totalCount ?? prev.totalDownloadFiles,
      };

    case UploadProcessingEventType.FileDeduplicated:
      return {
        ...prev,
        hashedFiles: prev.hashedFiles + 1,
        duplicateCount: data.isDuplicate ? prev.duplicateCount + 1 : prev.duplicateCount,
      };

    case UploadProcessingEventType.EmailUnpackingSkipped:
      return {
        ...prev,
        unpackingSkippedCount: prev.unpackingSkippedCount + 1,
      };

    case UploadProcessingEventType.ExtractingStarted: {
      // ZIP containers are never counted in totalFiles (they have no file records and
      // are excluded from files-queued counts), so no subtraction is needed here.
      // The extracted children will be counted via 'files-queued' events as batches are processed.
      const existingExtraction = prev.extractions.find((e) => e.filename === data.filename);
      return {
        ...prev,
        extractions: existingExtraction
          ? prev.extractions.map((e) =>
              e.filename === data.filename
                ? {
                    ...e,
                    totalEntries: data.totalEntries ?? 0,
                    completedEntries: 0,
                    isComplete: false,
                    completedAt: undefined,
                  }
                : e,
            )
          : [
              ...prev.extractions,
              {
                filename: data.filename,
                totalEntries: data.totalEntries ?? 0,
                completedEntries: 0,
                isComplete: false,
                completedAt: undefined,
              },
            ],
      };
    }

    case UploadProcessingEventType.FilesQueued:
      // Extraction discovered and queued a batch of files — grow the total incrementally.
      return {
        ...prev,
        totalFiles: prev.totalFiles + (data.count ?? 0),
      };

    case UploadProcessingEventType.ExtractingProgress:
      return {
        ...prev,
        extractions: prev.extractions.map((e) =>
          e.filename === data.filename ? { ...e, completedEntries: data.completedEntries ?? e.completedEntries } : e,
        ),
      };

    case UploadProcessingEventType.ExtractingComplete:
      return {
        ...prev,
        extractions: prev.extractions.map((e) =>
          e.filename === data.filename ? { ...e, isComplete: true, completedAt: Date.now() } : e,
        ),
      };

    case UploadProcessingEventType.FileError:
      return {
        ...prev,
        errors: [
          ...prev.errors,
          {
            filename: data.filename,
            rejectedReason: data.rejectedReason,
          },
        ],
      };

    case UploadProcessingEventType.ProcessingComplete:
      return {
        ...prev,
        status: 'completed',
      };

    case UploadProcessingEventType.ProcessingFailed:
      return {
        ...prev,
        status: 'failed',
      };

    default:
      return prev;
  }
}

function applyInitialState(data: any): UploadProgressState {
  return {
    status: data.status ?? 'processing',
    extractions: [],
    totalFiles: data.totalFiles ?? 0,
    hashedFiles: data.hashedFiles ?? 0,
    duplicateCount: data.duplicateCount ?? 0,
    unpackingSkippedCount: data.unpackingSkippedCount ?? 0,
    errors: (data.errors ?? []).map((e: any) => ({
      id: e.id,
      filename: e.filename,
      rejectedReason: e.rejectedReason,
      parentFileId: e.parentFileId,
      createdDate: e.createdDate,
    })),
    downloadedFiles: 0,
    totalDownloadFiles: 0,
    preparingFetchedCount: 0,
    preparingTotalCount: 0,
  };
}

/**
 * Multiplexed upload progress hook — opens a SINGLE SSE connection for all upload IDs,
 * dispatches events to per-upload state via the `uploadId` tag on each message.
 */
export const useMultiUploadProgress = (uploadIds: string[]) => {
  const [stateMap, setStateMap] = useState<Map<string, UploadProgressState>>(new Map());
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const sseRef = useRef<SSE | null>(null);
  const retryCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { fetchConfigGET } = useGetFetchConfig();

  // Throttle: buffer messages per uploadId, flush at most once per THROTTLE_INTERVAL_MS
  const bufferRef = useRef<Map<string, any[]>>(new Map());
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable sorted key for dependency tracking
  const uploadIdsKey = useMemo(() => [...uploadIds].sort().join(','), [uploadIds]);

  const flushBuffer = useCallback(() => {
    throttleTimerRef.current = null;
    const buffers = bufferRef.current;
    if (buffers.size === 0) return;

    // Snapshot and clear
    const snapshot = new Map(buffers);
    bufferRef.current = new Map();

    setStateMap((prev) => {
      const next = new Map(prev);
      for (const [uid, messages] of snapshot) {
        const current = next.get(uid) ?? initialState;
        next.set(uid, messages.reduce(reduceProgressEvent, current));
      }
      return next;
    });
  }, []);

  const clearThrottle = useCallback(() => {
    if (throttleTimerRef.current) {
      clearTimeout(throttleTimerRef.current);
      throttleTimerRef.current = null;
    }
    bufferRef.current = new Map();
  }, []);

  const enqueueMessage = useCallback(
    (uploadId: string, data: any) => {
      const buf = bufferRef.current.get(uploadId) ?? [];
      buf.push(data);
      bufferRef.current.set(uploadId, buf);

      // If no flush is scheduled, flush immediately and start cooldown
      if (throttleTimerRef.current === null) {
        flushBuffer();
        throttleTimerRef.current = setTimeout(() => {
          flushBuffer();
        }, THROTTLE_INTERVAL_MS);
      }
    },
    [flushBuffer],
  );

  const resetUpload = useCallback((uploadId: string) => {
    bufferRef.current.delete(uploadId);
    setStateMap((prev) => {
      const next = new Map(prev);
      next.delete(uploadId);
      return next;
    });
  }, []);

  const closeSSE = useCallback((shouldReconnect: boolean = true) => {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    if (shouldReconnect && retryCountRef.current < MAX_RETRIES) {
      const delay = BASE_DELAY_MS * Math.pow(2, retryCountRef.current);
      retryCountRef.current += 1;

      reconnectTimeoutRef.current = setTimeout(() => {
        setReconnectTrigger((prev) => prev + 1);
      }, delay);
    }
  }, []);

  // Reset retry count only when the set of upload IDs changes — not on every reconnect,
  // otherwise the backoff stays at the first delay and MAX_RETRIES is never hit.
  useEffect(() => {
    retryCountRef.current = 0;
  }, [uploadIdsKey]);

  useEffect(() => {
    if (uploadIds.length === 0) return;

    const setupSSE = () => {
      try {
        const sseUrl = `${APIBaseChronos}/api/storage/upload-progress-multi?uploadIds=${encodeURIComponent(uploadIds.join(','))}`;

        const eventSource = new SSE(sseUrl, {
          headers: Object.fromEntries(fetchConfigGET.headers.entries()),
        });

        eventSource.onmessage = (event: any) => {
          retryCountRef.current = 0;

          try {
            if (!event.data || !event.data.trim()) return;

            const data = JSON.parse(event.data);

            // Handle ping/heartbeat
            if (data.type === 'ping') return;

            // Ignore connected event (used by dynamic subscription, not needed here)
            if (data.type === 'connected') return;

            const uploadId = data.uploadId as string | undefined;
            if (!uploadId) return;

            // Handle initial state from DB — apply immediately (it's the baseline)
            if (data.type === 'initial-state') {
              const state = applyInitialState(data);
              setStateMap((prev) => {
                const next = new Map(prev);
                next.set(uploadId, state);
                return next;
              });
              return;
            }

            // Throttle all other messages
            enqueueMessage(uploadId, data);
          } catch (error) {
            console.error('Error parsing upload progress SSE message:', error);
          }
        };

        eventSource.onerror = () => {
          closeSSE();
        };

        sseRef.current = eventSource;
      } catch (error) {
        console.error('Error setting up upload progress SSE:', error);
        closeSSE();
      }
    };

    setupSSE();

    return () => {
      clearThrottle();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [uploadIdsKey, reconnectTrigger, fetchConfigGET, enqueueMessage, clearThrottle, closeSSE]);

  return { stateMap, resetUpload };
};
